import clsx from "clsx";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";

import { ContinuousUpdateStatus, ContinuousUpdateStatusImage } from "@/components/list/Schedule/constants";
import { ContinuousUpdateStatusType } from "@/components/list/Schedule/types";

type Props = {
  children?: ReactNode;
  label?: string;
  className?: string;
  align?: "center" | "left";
  editable?: boolean;
  overflowLength?: number;
  setTooltip?: (text?: string) => void;
  historyAlertColor?: string;
  disableContinuousUpdate?: boolean;
  continuousUpdateStatus?: ContinuousUpdateStatusType;
  isLoading?: boolean;
  onClick?: () => void;
  editing?: boolean;
  onEnter?: () => void;
  // テキスト入力でエラーがある場合はフォーカスを外さないように制御するためonBlurにElementを渡す
  onBlur?: (value: string, e: HTMLTextAreaElement) => void;
  onClickHistory?: () => void;
  ["data-test-id"]?: string;
};

export const GridCell: FC<Props> = ({
  children,
  label,
  className,
  align,
  editable,
  overflowLength,
  setTooltip,
  historyAlertColor,
  disableContinuousUpdate,
  continuousUpdateStatus,
  isLoading,
  onClick,
  editing,
  onEnter,
  onBlur,
  onClickHistory,
  "data-test-id": dataTestId,
}) => {
  const isOverflow = useMemo(
    () => (overflowLength ? isOverCharacters(label, overflowLength) : isOver30Characters(label) || isOver4Lines(label)),
    [label, overflowLength]
  );
  const isContinuousUpdateMode =
    continuousUpdateStatus !== undefined && continuousUpdateStatus !== ContinuousUpdateStatus.OFF;
  const [isHovered, setIsHovered] = useState(false);

  const [text, setText] = useState(label);
  useEffect(() => {
    if (!onEnter || editing) return;
    setText(label);
  }, [label, onEnter, editing]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    isOverflow && setTooltip(label);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    isOverflow && setTooltip(null);
  };

  return (
    <>
      {isLoading ? (
        // 変更後ローディング中
        <div className={className} style={{ backgroundColor: "#7f7f7f" }}>
          <div className="continuous-update-loading" />
        </div>
      ) : editing ? (
        // 編集中（セル上で直接編集する）
        <div className={clsx(className, "note-editor")}>
          <textarea
            draggable={false}
            value={text ?? ""}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => onBlur(text, e.currentTarget)}
            autoFocus={true}
            data-test-id={dataTestId}
          />
        </div>
      ) : (
        // 表示
        <div
          data-tip
          data-for="schedule-cell-tooltip"
          className={clsx(
            className,
            "grid-cell",
            isContinuousUpdateMode
              ? !editable || disableContinuousUpdate
                ? "schedule-cursor-nix"
                : `continuous-editable schedule-cursor-${ContinuousUpdateStatusImage[continuousUpdateStatus]}`
              : editable
              ? "editable"
              : "cursor-default"
          )}
          style={historyAlertColor ? { backgroundColor: historyAlertColor } : undefined}
          onMouseOver={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-test-id={dataTestId}
        >
          <div
            className={clsx("w-full h-full pointer-events-auto flex items-center break-all", align ?? "center")}
            onClick={() => {
              if (editable && (!isContinuousUpdateMode || !disableContinuousUpdate)) {
                onClick?.();
                onEnter?.();
              }
            }}
          >
            {children ? children : <>{substringValue(label)}</>}
          </div>
          {historyAlertColor && isHovered && (
            <div
              style={{ position: "absolute" }}
              className="top-[3px] right-[3px] w-[14px] h-[14px] rounded-full flex justify-center items-center cursor-pointer pointer-events-auto"
              onClick={(e) => {
                e.stopPropagation();
                onClickHistory();
              }}
            >
              <img src="../img/icon-history-black.svg" />
            </div>
          )}
        </div>
      )}
    </>
  );
};

const MAX_LINES = 4;
const ELLIPSIS = "...";

const isOverCharacters = (value: string, length: number) => {
  return length < (value ?? "").length;
};
const isOver30Characters = (value?: string) => {
  return 30 < (value ?? "").length;
};

const isOver4Lines = (value?: string) => {
  // 改行を含む場合、4行まで表示できる
  return MAX_LINES <= (value ?? "").match(/\n/g)?.length;
};

const substringValue = (value?: string) => {
  const v = value ?? "";
  if (isOver4Lines(v)) {
    return value.split("\n").splice(0, MAX_LINES).join("\n") + ELLIPSIS;
  }
  if (isOver30Characters(v)) {
    return value.substring(0, 30) + ELLIPSIS;
  }

  return v;
};
